/**
 * heroWire — the wire shape of /api/cms/public/hero-slides, and the
 * conversion to the snapshot shape (`HeroSlideEntry`).
 *
 * WHY THE CONVERSION EXISTS
 * -------------------------
 * The public endpoint collapses the per-locale columns into ONE flat set of
 * strings for the requested locale:
 *
 *   { id, sort_order, is_visible, icon, gradient_token, image_position,
 *     cta_link, image_media_uuid, tag, title1, title2, text, cta, image_alt }
 *
 * The snapshot the build writes (`hero-slides.generated.ts`) carries the same
 * facts in a DIFFERENT shape — camelCase, and the copy NESTED under a
 * per-locale map:
 *
 *   { id, sortOrder, isVisible, icon, gradientToken, imagePosition,
 *     ctaLink, imageMediaUuid, copy: { 'zh-Hant': { tag, title1, … } } }
 *
 * `scripts/cms-sync.ts` performed this conversion while building the
 * snapshot. The runtime path needs it too — a component that reads the wire
 * payload as if it were a snapshot entry finds `isVisible` undefined, filters
 * every slide away, and renders an empty hero.
 *
 * The conversion lives here so the runtime hook and the prerender pass cannot
 * drift: both call `heroWireToEntry`.
 */

import type { HeroLocale, HeroSlideCopy, HeroSlideEntry } from '@/content/hero-types'

/** One slide as the public endpoint serves it. */
export interface HeroSlideWire {
  id: number
  sort_order: number
  is_visible: boolean
  icon: string
  gradient_token: string
  image_position: string
  cta_link: string
  image_media_uuid: string | null
  tag: string
  title1: string
  title2: string
  text: string
  cta: string
  image_alt: string
}

export interface HeroSlidesWireResp {
  slides?: HeroSlideWire[]
}

/**
 * Convert one wire slide into a snapshot-shaped entry.
 *
 * `locale` is the locale the payload was fetched for; the copy is filed under
 * that key so `copy[locale]` resolves. The renderer's fallback chain
 * (`copy[locale] ?? copy['zh-Hant'] ?? copy.en`) covers the other locales
 * until the next fetch lands.
 */
export function heroWireToEntry(raw: HeroSlideWire, locale: HeroLocale): HeroSlideEntry {
  const copy: HeroSlideCopy = {
    tag: raw.tag,
    title1: raw.title1,
    title2: raw.title2,
    text: raw.text,
    cta: raw.cta,
    imageAlt: raw.image_alt,
  }
  return {
    id: raw.id,
    sortOrder: raw.sort_order,
    isVisible: raw.is_visible,
    icon: raw.icon,
    gradientToken: raw.gradient_token,
    imagePosition: raw.image_position,
    ctaLink: raw.cta_link,
    imageMediaUuid: raw.image_media_uuid,
    copy: { [locale]: copy },
  }
}

/**
 * Convert a whole wire payload into snapshot-shaped entries, in the server's
 * order (sort_order, id — the server already sorts, so the array order is
 * preserved as-is).
 */
export function heroWireToEntries(
  slides: HeroSlideWire[],
  locale: HeroLocale,
): HeroSlideEntry[] {
  return slides.map((s) => heroWireToEntry(s, locale))
}
